import React, { useEffect, useRef, useState } from 'react';
import { useCompact, useTapShield } from './_layout';
import { playWord, playYummy, playOops } from './_audio';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5', purple: '#A06CD5',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';

const ASSETS = '/games/vocab_monster_sound';

const ANIM_CSS = `
@keyframes mtBob { 0%,100%{transform:translate(-50%, 0)} 50%{transform:translate(-50%, -8px)} }
@keyframes mtFloat0 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(4px,-10px)} }
@keyframes mtFloat1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-6px,-12px)} }
@keyframes mtFloat2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(8px,-8px)} }
@keyframes mtFloat3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-4px,-14px)} }
@keyframes mtYummyPop { 0%{transform:scale(0) rotate(-20deg); opacity:0} 60%{transform:scale(1.2) rotate(-5deg); opacity:1} 100%{transform:scale(1) rotate(-5deg); opacity:1} }
@keyframes mtSparkle { 0%,100%{opacity:.4; transform:scale(.85) rotate(0)} 50%{opacity:1; transform:scale(1.1) rotate(15deg)} }
@keyframes mtFlyToMouth { 0%{transform:translate(0,0) scale(1) rotate(0)} 100%{transform:translate(var(--mtx),var(--mty)) scale(0.2) rotate(720deg); opacity:0} }
@keyframes mtSpitOut { 0%{transform:translate(0,0) scale(0.4); opacity:0} 40%{opacity:1} 100%{transform:translate(var(--mtx),var(--mty)) scale(1); opacity:0.4} }

/* Monster mouth crossfade: 2 medium-open poses oscillate smoothly while idle.
   Each layer fades 0 → 1 → 0 in a 1.8s cycle, offset by 0.9s so when one is
   peak the other is trough. */
@keyframes mtMouthA { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes mtMouthB { 0%,100%{opacity:0} 50%{opacity:1} }

.mt-monster .mt-pose { position:absolute; inset:0; width:100%; height:auto; display:block; pointer-events:none; transition: opacity 0.35s ease; filter: drop-shadow(0 6px 12px rgba(0,0,0,0.18)); }

.mt-monster.idle .mt-pose-low  { animation: mtMouthA 1.8s ease-in-out infinite; }
.mt-monster.idle .mt-pose-high { animation: mtMouthB 1.8s ease-in-out infinite; }
.mt-monster.idle .mt-pose-open    { opacity: 0; }
.mt-monster.idle .mt-pose-closed  { opacity: 0; }

.mt-monster.correct .mt-pose-low  { opacity: 0; animation: none; }
.mt-monster.correct .mt-pose-high { opacity: 0; animation: none; }
.mt-monster.correct .mt-pose-open    { opacity: 1; }
.mt-monster.correct .mt-pose-closed  { opacity: 0; }

.mt-monster.wrong .mt-pose-low  { opacity: 0; animation: none; }
.mt-monster.wrong .mt-pose-high { opacity: 0; animation: none; }
.mt-monster.wrong .mt-pose-open    { opacity: 0; }
.mt-monster.wrong .mt-pose-closed  { opacity: 1; }
`;

function injectCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('monster-anim-css')) return;
  const el = document.createElement('style');
  el.id = 'monster-anim-css';
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

// Bubble positions relative to stage, anchored around the monster (center-bottom)
const POSITIONS_4 = [
  { left: '6%',  top: '24%' },  // top-left
  { right: '6%', top: '24%' },  // top-right
  { left: '8%',  bottom: '14%' }, // bottom-left
  { right: '8%', bottom: '14%' }, // bottom-right
];
const POSITIONS_3 = [
  { left: '50%', top: '12%', transform: 'translateX(-50%)' },
  { left: '6%',  bottom: '14%' },
  { right: '6%', bottom: '14%' },
];

export default function MonsterTapGame({ payload, onAnswer, disabled }) {
  const shield = useTapShield(600);
  const compact = useCompact();
  const isSound = payload.cue_kind === 'sound';
  const bubbles = payload.bubbles || [];
  const positions = bubbles.length === 3 ? POSITIONS_3 : POSITIONS_4;

  const [poppedId, setPoppedId] = useState(null);
  const [resultKind, setResultKind] = useState(null); // 'yum' | 'blehh' | null
  const [locked, setLocked] = useState(false);
  const stageRef = useRef(null);
  const monsterRef = useRef(null);

  // CSS class on monster — drives opacity transitions on the 4 pose layers
  const monsterState = resultKind === 'yum' ? 'correct'
                     : resultKind === 'blehh' ? 'wrong'
                     : 'idle';

  useEffect(() => { injectCss(); }, []);

  // Auto-play vocab audio once per word (sound mode only)
  useEffect(() => {
    if (!isSound) return;
    let cleanup = null;
    const t = setTimeout(() => {
      cleanup = playWord(payload.audio_url, payload.tts_text || payload.word_en);
    }, 400);
    return () => { clearTimeout(t); if (cleanup) cleanup(); };
  }, [isSound, payload.audio_url, payload.tts_text, payload.word_en]);

  // Reaction sound on result
  useEffect(() => {
    if (resultKind === 'yum') playYummy();
    if (resultKind === 'blehh') playOops();
  }, [resultKind]);

  const replay = () => playWord(payload.audio_url, payload.tts_text || payload.word_en);

  const handleTap = (bubble) => {
    if (!shield.ok()) return; // ghost tap from the previous question
    if (locked || disabled) return;
    setLocked(true);
    setPoppedId(bubble.id);

    // Compute flight vector from bubble to monster mouth (approx mid-y of monster)
    if (stageRef.current && monsterRef.current) {
      const monRect = monsterRef.current.getBoundingClientRect();
      const bubbleEl = stageRef.current.querySelector(`[data-bubble-id="${bubble.id}"]`);
      if (bubbleEl) {
        const bRect = bubbleEl.getBoundingClientRect();
        const dx = (monRect.left + monRect.width / 2) - (bRect.left + bRect.width / 2);
        const dy = (monRect.top + monRect.height * 0.45) - (bRect.top + bRect.height / 2);
        bubbleEl.style.setProperty('--mtx', `${dx}px`);
        bubbleEl.style.setProperty('--mty', `${dy}px`);
      }
    }

    const isCorrect = bubble.id === payload.correct_bubble_id;
    setTimeout(() => {
      setResultKind(isCorrect ? 'yum' : 'blehh');
    }, 600);

    setTimeout(() => {
      if (onAnswer) onAnswer({
        is_correct: isCorrect,
        user_answer: bubble.tag_code,
      });
    }, 1700);
  };

  return (
    <div style={{
      width: 'calc(100vw - 32px)',
      maxWidth: 1600,
      marginLeft: 'calc(-50vw + 50% + 16px)',
      marginRight: 'calc(-50vw + 50% + 16px)',
      fontFamily: FONT_BODY,
      padding: 0,
      height: 'calc(100vh - 120px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div
        ref={stageRef}
        style={{
          position: 'relative',
          width: compact ? '100%' : 'min(100%, calc((100vh - 120px) * 1376 / 768))',
          aspectRatio: compact ? '4 / 5' : '1376 / 768',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(139,111,71,0.18)',
          userSelect: 'none',
        }}
      >
        {/* Background — playground/forest scene */}
        <img
          src={`${ASSETS}/background.png`}
          alt=""
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', pointerEvents: 'none',
          }}
        />

        {/* Sparkle stars decoration */}
        {['12%', '32%', '60%', '85%'].map((l, i) => (
          <div key={i} style={{
            position: 'absolute',
            top: `${6 + (i * 3) % 14}%`, left: l,
            fontSize: 'clamp(16px, 2vw, 24px)',
            animation: `mtSparkle ${2 + i * 0.35}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
            zIndex: 1, pointerEvents: 'none',
          }}>⭐</div>
        ))}

        {/* Cue area top center: sound button OR thought bubble (definition) */}
        <div style={{
          position: 'absolute', top: 16, left: 16, right: 16,
          display: 'flex', justifyContent: 'center', zIndex: 4,
          pointerEvents: 'none',
        }}>
          {isSound ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <button
                onClick={replay}
                style={{
                  pointerEvents: 'auto',
                  padding: '12px 22px', borderRadius: 999, border: 'none',
                  background: HT.coralDark, color: HT.white,
                  fontSize: 'clamp(13px, 1.6vw, 16px)',
                  fontWeight: 700, fontFamily: FONT_DISPLAY,
                  boxShadow: `0 4px 0 ${HT.coralPress}`,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                }}
              >
                🔊 Nghe quái vật phát âm
              </button>
              {/* Vietnamese meaning — lets kids who can't play the audio still pick
                  the right picture. Older payloads lack name_vi; recover it from
                  explanation_vi ("Đó là 'word' (nghĩa)."). */}
              {(() => {
                const vi = payload.name_vi
                  || (payload.explanation_vi || '').match(/\(([^)]+)\)\.?\s*$/)?.[1]
                  || '';
                return vi ? (
                  <div style={{
                    padding: '6px 16px', borderRadius: 999,
                    background: HT.white, border: `2px solid ${HT.yellow}`,
                    boxShadow: `0 2px 0 ${HT.yellowShadow}`,
                    fontSize: 'clamp(13px, 1.6vw, 16px)', fontWeight: 700,
                    color: HT.brownDark, fontFamily: FONT_BODY,
                  }}>{vi}</div>
                ) : null;
              })()}
            </div>
          ) : (
            <div style={{
              pointerEvents: 'auto',
              maxWidth: 'min(460px, 78%)',
              padding: '10px 18px',
              background: HT.white, color: HT.text,
              borderRadius: 18, border: `2px solid ${HT.yellow}`,
              boxShadow: `0 3px 0 ${HT.yellowShadow}`,
              fontSize: 'clamp(12px, 1.5vw, 15px)',
              fontWeight: 600, lineHeight: 1.4, textAlign: 'center',
              position: 'relative',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800, color: HT.coralDark,
                letterSpacing: 1.5, marginBottom: 4,
              }}>🧠 QUÁI VẬT NGHĨ:</div>
              {payload.definition_en ? (
                <div style={{
                  fontStyle: 'italic',
                  color: HT.textSoft,
                  fontSize: 'clamp(11px, 1.35vw, 14px)',
                  marginBottom: 4,
                }}>"{payload.definition_en}"</div>
              ) : null}
              <div>{payload.definition_vi || payload.word_en}</div>
              {/* Pointer tail */}
              <div style={{
                position: 'absolute', bottom: -10, left: '50%',
                transform: 'translateX(-50%)',
                width: 0, height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: `10px solid ${HT.yellow}`,
              }} />
            </div>
          )}
        </div>

        {/* Bubbles with vocab images */}
        {bubbles.map((b, idx) => {
          const pos = positions[idx] || positions[positions.length - 1];
          const isPopped = poppedId === b.id;
          const showSpit = resultKind === 'blehh' && isPopped;
          return (
            <div
              key={b.id}
              data-bubble-id={b.id}
              onClick={() => handleTap(b)}
              style={{
                position: 'absolute',
                ...pos,
                width: 'clamp(80px, 13%, 140px)',
                aspectRatio: '1',
                cursor: (locked || disabled) ? 'default' : 'pointer',
                animation: isPopped
                  ? (showSpit ? 'mtSpitOut 1s ease-out 0.4s forwards' : 'mtFlyToMouth 0.7s ease-in forwards')
                  : `mtFloat${idx % 4} ${2.4 + idx * 0.2}s ease-in-out infinite`,
                zIndex: 2,
                userSelect: 'none',
              }}
            >
              {/* Bubble shell (transparent PNG) */}
              <img
                src={`${ASSETS}/bubble.png`}
                alt=""
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12))',
                }}
              />
              {/* Vocab image inside bubble */}
              {b.image_url ? (
                <img
                  src={b.image_url}
                  alt=""
                  style={{
                    position: 'absolute',
                    top: b.word_en ? '10%' : '15%',
                    left: '15%',
                    width: '70%',
                    height: b.word_en ? '58%' : '70%',
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))',
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 32,
                }}>❓</div>
              )}

              {/* Word label inside the bubble (only in def mode — bubbles carry word_en) */}
              {b.word_en ? (
                <div style={{
                  position: 'absolute',
                  left: '12%', right: '12%',
                  bottom: '14%',
                  textAlign: 'center',
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 800,
                  color: HT.brownDark,
                  fontSize: 'clamp(11px, 1.3vw, 16px)',
                  lineHeight: 1.1,
                  textShadow: '0 1px 2px rgba(255,255,255,0.9)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>{b.word_en}</div>
              ) : null}
            </div>
          );
        })}

        {/* Monster — bottom center. 4 stacked pose layers with crossfade transitions.
            In idle: character_2 ↔ character_3 oscillate (mouth opening rhythm).
            Correct: fades to charactor_open_mouth (huge open).
            Wrong: fades to character_1 (closed mouth). */}
        <div
          ref={monsterRef}
          className={`mt-monster ${monsterState}`}
          style={{
            position: 'absolute',
            left: '50%', bottom: '4%',
            transform: 'translateX(-50%)',
            width: 'clamp(180px, 30%, 320px)',
            zIndex: 3,
            animation: 'mtBob 2s ease-in-out infinite',
          }}
        >
          {/* Reference image to set the container's intrinsic ratio.
              Visually invisible — opacity 0 so only stacked poses show. */}
          <img
            src={`${ASSETS}/character_2.png`}
            alt=""
            style={{
              width: '100%', display: 'block',
              opacity: 0, pointerEvents: 'none',
            }}
          />
          <img className="mt-pose mt-pose-low"    src={`${ASSETS}/character_2.png`} alt="" />
          <img className="mt-pose mt-pose-high"   src={`${ASSETS}/character_3.png`} alt="" />
          <img className="mt-pose mt-pose-open"   src={`${ASSETS}/charactor_open_mouth.png`} alt="" />
          <img className="mt-pose mt-pose-closed" src={`${ASSETS}/character_1.png`} alt="" />
        </div>

        {/* Yummy! reaction speech bubble (top-right) */}
        {resultKind === 'yum' && (
          <div style={{
            position: 'absolute',
            top: '24%', right: '14%',
            padding: '8px 20px',
            background: HT.yellow,
            color: HT.brownDark,
            borderRadius: 999,
            border: `3px solid ${HT.white}`,
            fontSize: 'clamp(16px, 2.4vw, 26px)',
            fontWeight: 800,
            fontFamily: FONT_DISPLAY,
            boxShadow: `0 4px 0 ${HT.yellowShadow}`,
            zIndex: 5,
            animation: 'mtYummyPop 0.5s ease-out forwards',
            transform: 'rotate(-5deg)',
            whiteSpace: 'nowrap',
          }}>Yummy! 😋</div>
        )}

        {/* Blehh reaction (also top-right when wrong) */}
        {resultKind === 'blehh' && (
          <div style={{
            position: 'absolute',
            top: '24%', right: '14%',
            padding: '8px 20px',
            background: HT.coral,
            color: HT.white,
            borderRadius: 999,
            border: `3px solid ${HT.white}`,
            fontSize: 'clamp(16px, 2.4vw, 26px)',
            fontWeight: 800,
            fontFamily: FONT_DISPLAY,
            boxShadow: `0 4px 0 ${HT.coralPress}`,
            zIndex: 5,
            animation: 'mtYummyPop 0.5s ease-out forwards',
            transform: 'rotate(-5deg)',
            whiteSpace: 'nowrap',
          }}>Blehh! 🤢</div>
        )}

        {/* Status overlay — floats above stage bottom instead of pushing layout */}
        {resultKind && (
          <div style={{
            position: 'absolute',
            left: '50%', bottom: '3%',
            transform: 'translateX(-50%)',
            maxWidth: '88%',
            padding: '10px 18px',
            background: resultKind === 'yum' ? HT.greenBg : HT.pinkBg,
            border: `2px solid ${resultKind === 'yum' ? HT.greenDark : HT.coral}`,
            borderRadius: 14, textAlign: 'center',
            fontSize: 'clamp(12px, 1.4vw, 16px)',
            fontWeight: 700, color: HT.text,
            fontFamily: FONT_BODY,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            zIndex: 6,
          }}>
            {resultKind === 'yum' ? '✨ Tuyệt vời! ' : '😅 Lần sau nhé! '}
            Đáp án đúng là <b style={{ color: HT.coralDark }}>{payload.word_en}</b>
            {payload.explanation_vi ? ` — ${payload.explanation_vi}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
